import { useTokenStore } from "@/lib/zustand";
import { useSwapAccount } from "../use-swap-account";
import { useMemo } from "react";
import { ChainType } from "@lifi/sdk";
import { isSolanaAddress } from "@/lib/solana";
import { useAccount as useWagmiAccount } from "wagmi";

interface UseSwapDisabledProps {
    evmAddress: string;
    solanaAddress: string;
    isSVMtoEvm: boolean;
    isEvmToSVM: boolean;
    isLoadingRoutes: boolean;
    isLoadingSwap: boolean;
}

export const useSwapDisabled = ({ evmAddress, solanaAddress, isSVMtoEvm, isEvmToSVM, isLoadingRoutes, isLoadingSwap }: UseSwapDisabledProps) => {
    const { address, caipAddress, isConnected } = useSwapAccount();
    const { address: wagmiAddress } = useWagmiAccount();
    const { fromToken, fromTokenAmount, toToken, fromChain } = useTokenStore();

    const isSwapDisabled = useMemo(() => {
        return isLoadingRoutes ||
            isLoadingSwap ||
            !isConnected ||
            // (!connected && fromChain.chainType === ChainType.SVM) ||
            !address ||
            !fromToken ||
            Number(fromTokenAmount) === 0 ||
            !fromTokenAmount ||
            !toToken ||
            (isSVMtoEvm &&
                (!evmAddress ||
                    !evmAddress.startsWith("0x") ||
                    evmAddress.length !== 42)) ||
            (isEvmToSVM && (!solanaAddress || !isSolanaAddress(solanaAddress))) ||
            (fromChain.chainType === ChainType.SVM &&
                caipAddress?.includes("eip155")) ||
            (fromChain.chainType === ChainType.EVM &&
                caipAddress?.includes("solana") &&
                !wagmiAddress);
    }, [isLoadingRoutes, isLoadingSwap, isConnected, address, fromToken, fromTokenAmount, toToken, isSVMtoEvm, evmAddress, isEvmToSVM, solanaAddress, fromChain, caipAddress, wagmiAddress]);

    const getSwapDisabledReason = useMemo(() => {
        if (!isConnected || !address) {
            return "Please connect your wallet to swap";
        }
        if (isLoadingRoutes) {
            return "Loading routes...";
        }
        if (isLoadingSwap) {
            return "Swapping...";
        }
        if (!fromToken || !toToken) {
            return "Please select tokens to swap";
        }
        if (Number(fromTokenAmount) === 0 || !fromTokenAmount) {
            return "Please enter an amount to swap";
        }
        if (
            isSVMtoEvm &&
            (!evmAddress || !evmAddress.startsWith("0x") || evmAddress.length !== 42)
        ) {
            return "Please enter a valid EVM address";
        }
        if (isEvmToSVM && (!solanaAddress || !isSolanaAddress(solanaAddress))) {
            return "Please enter a valid Solana address";
        }
        if (
            fromChain.chainType === ChainType.SVM &&
            caipAddress?.includes("eip155")
        ) {
            return "Please connect a Solana wallet";
        }
        if (
            fromChain.chainType === ChainType.EVM &&
            caipAddress?.includes("solana") &&
            !wagmiAddress
        ) {
            return "Please connect an EVM wallet";
        }
        return null;
    }, [isConnected, address, isLoadingRoutes, isLoadingSwap, fromToken, fromTokenAmount, toToken, isSVMtoEvm, evmAddress, isEvmToSVM, solanaAddress, fromChain, caipAddress, wagmiAddress]);

    return { isSwapDisabled, getSwapDisabledReason };
}
